import i18n from "i18next";
import React, { Suspense, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useIdleTimer } from "react-idle-timer";
import { Route } from 'react-router';
import { Navigate, Routes } from 'react-router-dom';

import AlertBanner from './components/AlertBanner';
import NotificationAlert from './components/Alerts/NotificationAlert';
import CentredSpinner from "./components/Components/CentredSpinner";
import ScrollToTopButton from './components/Components/ScrollToTopButton';
import { SiteContextProvider } from './components/Contexts/SiteContextProvider';
import Footer from './components/Footer';
import NavMenu from './components/NavMenu';
import Admin from './components/Pages/Admin';
import Configuration from './components/Pages/Configuration';
import LotDetailPage from './components/Pages/LotDetails/LotDetailsPage';
import BaggingReport from './components/Pages/LotDetails/LotReports/BaggingReport';
import DetailsReport from './components/Pages/LotDetails/LotReports/DetailsReport';
import PackageReport from './components/Pages/LotDetails/LotReports/PackageReport';
import LotProcessDataReport from './components/Pages/LotDetails/LotReports/LotProcessDataReport';
import LotList from './components/Pages/LotList/LotList';
import Reporting from './components/Pages/Reporting';
import RecipeReport from './components/Pages/Reporting/RecipeReport';
import UserPreferences from './components/Pages/UserPreferences';
import ProtectedRoute from './components/ProtectedRoute';
import { signOut } from './components/Utilities/AuthUtils';
import { useHttpUtils } from './components/Utilities/useHttpUtils';
import { useSiteCode } from './components/Utilities/useSiteCode';
import { useUserAuthorization } from "./components/Utilities/useUserAuthorization";

import './custom.css';

function LegacyApp(props) {

    const { t } = props;
    const { fetchDataAuthenticated } = useHttpUtils();
    const { checkingAuthorization, userIsAuthorized } = useUserAuthorization();

    const defaultIdleTimeout = 30;
    const [idleTimeout, setIdleTimeout] = useState(defaultIdleTimeout);
    useIdleTimer({
        onIdle: () => signOut(),
        timeout: 1000 * 60 * idleTimeout
    });

    const siteCode = useSiteCode();

    useEffect(() => {
        if (siteCode !== undefined) {
            console.log(`Changing translations for site namespace '${siteCode}'`);
            i18n.setDefaultNamespace(siteCode.toLowerCase());
            i18n.changeLanguage(i18n.language);
        }
    }, [siteCode]);

    useEffect(() => {
        if (!checkingAuthorization && userIsAuthorized) {

            fetchDataAuthenticated('api/config/idletimeout')
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error();
                })
                .then(data => {
                    setIdleTimeout(data);
                })
                .catch(() => {
                    console.log(`Failed to get idle timeout from config. Setting default value '${defaultIdleTimeout}'`);
                });
        }
    }, [checkingAuthorization, userIsAuthorized, fetchDataAuthenticated]);

    if (checkingAuthorization) {
        return (
            <CentredSpinner className="mt-3" translationKey="Authorization.CheckingUserAuthorization" />
        );
    }

    if (!userIsAuthorized) {
        return (
            <div className="d-flex justify-content-center mt-3">
                <h5>{t("Authorization.UnauthorizedMessage")}</h5>
            </div>
        );
    }

    return (
        <SiteContextProvider>
            <Suspense fallback={<CentredSpinner />}>
                <title>{t("Application.TabTitle")}</title>
                <NotificationAlert />
                <AlertBanner variant={"danger"} />
                <NavMenu />
                <ScrollToTopButton />
                <Routes>
                    <Route path='/' exact element={<Navigate to="/lots" replace />} />
                    <Route path='/lots' exact element={<LotList />} />
                    <Route path='/lots/ReadyToReview' exact element={<LotList status="ReadyToReview" tableName={"lots-table-ready-to-review"} />} />
                    <Route path='/lots/ReadyToGrade' exact element={<LotList status="ReadyToGrade" tableName={"lots-table-ready-to-grade"} />} />
                    <Route path='/lotdetails/:lotNumber' element={<LotDetailPage />} />
                    <Route path='/lotdetails/:lotNumber/baggingReport' element={<BaggingReport />} />
                    <Route path='/lotdetails/:lotNumber/detailsReport' element={<DetailsReport />} />
                    <Route path='/lotdetails/:lotNumber/packageReport' element={<PackageReport />} />
                    <Route path='/lotdetails/:lotNumber/lotProcessDataReport' element={<LotProcessDataReport />} />
                    <Route path='/recipe/recipeReport/:businessUnit' element={<RecipeReport />} />
                    <Route path='/config' exact element={<Configuration />} />
                    <Route path='/config/businessunits' element={<Configuration initialSelectedTab={"businessUnits"} />} />
                    <Route path='/config/configData' element={<Configuration initialSelectedTab={"configData"} />} />
                    <Route path='/config/equipmenttypes' element={<Configuration initialSelectedTab={"equipmentTypes"} />} />
                    <Route path='/config/equipment' element={<Configuration initialSelectedTab={"equipment"} />} />
                    <Route path='/config/qualities' element={<Configuration initialSelectedTab={"qualities"} />} />
                    <Route path='/config/lines' element={<Configuration initialSelectedTab={"lines"} />} />
                    <Route path='/config/linequalitydata' element={<Configuration initialSelectedTab={"lineQualityData"} />} />
                    <Route path='/config/labresultsconfig' element={<Configuration initialSelectedTab={"labResultsConfig"} />} />
                    <Route path='/config/recipetypes' element={<Configuration initialSelectedTab={"recipeTypes"} />} />
                    <Route path='/config/recipeproperties' element={<Configuration initialSelectedTab={"recipeProperties"} />} />
                    <Route path='/config/recipedatatypes' element={<Configuration initialSelectedTab={"recipeDataTypes"} />} />
                    <Route path='/config/recipedata' element={<Configuration initialSelectedTab={"recipeData"} />} />
                    <Route path='/config/recipes' element={<Configuration initialSelectedTab={"recipes"} />} />
                    <Route path='/config/gradedreasons' element={<Configuration initialSelectedTab={"gradedReasons"} />} />
                    <Route path='/config/emails' element={<Configuration initialSelectedTab={"emails"} />} />
                    <Route path='/config/packageTypes' element={<Configuration initialSelectedTab={"packageTypes"} />} />
                    <Route path='/config/recipeToPackageTypes' element={<Configuration initialSelectedTab={"recipeToPackageTypes"} />} />
                    <Route path='/config/changeLog' element={<ProtectedRoute privilege={"admin"} route={"/config"}><Configuration initialSelectedTab={"changeLog"} /></ProtectedRoute>} />
                    <Route path='/admin' element={<ProtectedRoute privilege={"admin"}><Admin /></ProtectedRoute>} />
                    <Route path='/user' element={<UserPreferences />} />
                    <Route path='/reporting' element={<Reporting />} />
                </Routes>
                <Footer />
            </Suspense>
        </SiteContextProvider>
    );
}

const WrappedLegacyApp = withTranslation()(LegacyApp);

// i18n translations might still be loaded by the http backend
// use react's Suspense
export default function App() {
    return (
        <Suspense fallback={<CentredSpinner />}>
            <WrappedLegacyApp />
        </Suspense>
    );
}